/**
 * Socket.IO client for real-time comment updates
 */
import { io, Socket } from 'socket.io-client';

let socket: Socket | null = null;

/**
 * Get or create the socket instance
 */
export const getSocket = (): Socket => {
  if (!socket) {
    const backendUrl = process.env.REACT_APP_API_URL || 'https://localhost:3000';
    
    socket = io(backendUrl, {
      transports: ['websocket', 'polling'],
      autoConnect: true,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 5,
      withCredentials: true,
    });

    socket.on('connect', () => {
      console.log('🔌 [SOCKET] Connected to server:', socket?.id);
    });

    socket.on('disconnect', (reason) => {
      console.log('🔌 [SOCKET] Disconnected from server:', reason);
    });

    socket.on('connect_error', (error) => {
      console.error('🔌 [SOCKET] Connection error:', error.message);
    });

    socket.on('reconnect', (attemptNumber) => {
      console.log('🔌 [SOCKET] Reconnected after', attemptNumber, 'attempts');
    });

    socket.on('reconnect_attempt', (attemptNumber) => {
      console.log('🔌 [SOCKET] Reconnection attempt:', attemptNumber);
    });

    socket.on('reconnect_failed', () => {
      console.error('🔌 [SOCKET] Failed to reconnect after maximum attempts');
    });
  }

  return socket;
};

/**
 * Join a page-specific room for real-time updates
 */
export const joinPageRoom = (pageId: string): void => {
  const socket = getSocket();
  if (socket && socket.connected) {
    socket.emit('join-page', pageId);
    console.log('🔌 [SOCKET] Joining room: page:' + pageId);
  } else {
    console.warn('🔌 [SOCKET] Cannot join room - socket not connected');
    // Try again when connected
    socket?.once('connect', () => {
      socket.emit('join-page', pageId);
      console.log('🔌 [SOCKET] Joining room after connect: page:' + pageId);
    });
  }
};

/**
 * Leave a page-specific room
 */
export const leavePageRoom = (pageId: string): void => {
  const socket = getSocket();
  if (socket && socket.connected) {
    socket.emit('leave-page', pageId);
    console.log('🔌 [SOCKET] Leaving room: page:' + pageId);
  }
};

/**
 * Listen for new comments
 */
export const onNewComment = (callback: (comment: any) => void): void => {
  const socket = getSocket();
  socket.on('new-comment', callback);
};

/**
 * Remove new comment listener
 */
export const offNewComment = (callback: (comment: any) => void): void => {
  const socket = getSocket();
  socket.off('new-comment', callback);
};

/**
 * Listen for comment status changes (e.g. from manual moderation on another client)
 */
export const onCommentUpdated = (
  callback: (update: {
    id: string;
    status: string;
    action: string;
    moderationResult?: any;
    autoReply?: any;
  }) => void
): void => {
  const socket = getSocket();
  socket.on('comment-updated', callback);
};

export const offCommentUpdated = (callback: (update: any) => void): void => {
  const socket = getSocket();
  socket.off('comment-updated', callback);
};

/**
 * Disconnect the socket
 */
export const disconnectSocket = (): void => {
  if (socket) {
    socket.disconnect();
    socket = null;
    console.log('🔌 [SOCKET] Disconnected and cleared');
  }
};

export default {
  getSocket,
  joinPageRoom,
  leavePageRoom,
  onNewComment,
  offNewComment,
  onCommentUpdated,
  offCommentUpdated,
  disconnectSocket,
};

